import { Plus } from "lucide-react";
import { useCallback } from "react";
import NoResult from "@/shared/components/NoResult";
import { useInfiniteScroll } from "@/shared/hooks/useInfiniteScroll";
import type { Supplier } from "@/shared/types/supplier.types";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { Skeleton } from "@/shared/ui/Skeleton";
import { useSuppliersData } from "../../hooks/useSuppliersData";
import SupplierListItem from "./SupplierListItem";

type SuppliersSelectionDrawerContentProps = {
  onSupplierSelected: (supplier: Supplier) => void
  onEditSupplier: (supplier: Supplier) => void
  onDeleteSupplier: (supplier: Supplier) => Promise<void>
  onCreateSupplier: () => void
};

const SKELETON_COUNT = 8;

const SupplierListSkeleton = () => (
  <div className="flex flex-col">
    {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
      <div key={`skeleton-${index}`} className="flex items-center justify-between p-3 border-b">
        <div className="flex-1">
          <Skeleton className="h-4 w-40 mb-1.5" />
          <Skeleton className="h-3 w-56" />
        </div>
        <div className="flex gap-1">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
    ))}
  </div>
);

const SuppliersSelectionDrawerContent = ({
  onSupplierSelected,
  onEditSupplier,
  onDeleteSupplier,
  onCreateSupplier,
}: SuppliersSelectionDrawerContentProps) => {
  const {
    search,
    setSearch,
    suppliersData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    listLoading,
  } = useSuppliersData();

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadMoreRef } = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    callback: handleFetchNextPage,
    resetScrollOnMount: false,
  });

  const hasSuppliers = suppliersData.length > 0;
  const showEmptyState = !hasSuppliers && !isLoading && !listLoading;

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-200px)]">
      <div className="flex flex-col gap-3 mb-4">
        <Input
          placeholder="Rechercher une entreprise"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="outline"
          className="w-full"
          onClick={onCreateSupplier}
        >
          <Plus size={16} />
          Ajouter une nouvelle entreprise
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {listLoading && <SupplierListSkeleton />}

        {showEmptyState && (
          <NoResult
            title="Aucune entreprise trouvée"
            text={search
              ? "Essayez de modifier votre recherche."
              : "Aucune entreprise disponible."}
          />
        )}

        {hasSuppliers && !listLoading && (
          <>
            {suppliersData.map((supplier) => (
              <SupplierListItem
                key={supplier.id}
                supplier={supplier}
                onSelect={onSupplierSelected}
                onEdit={onEditSupplier}
                onDelete={onDeleteSupplier}
              />
            ))}

            {hasNextPage && (
              <div ref={loadMoreRef} className="h-16">
                {isFetchingNextPage && (
                  <div className="flex justify-center py-4">
                    <Skeleton className="h-4 w-32" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SuppliersSelectionDrawerContent;
